import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import {
  Box,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Tab,
  Tabs,
  Typography
} from "@mui/material";

import { useTesterIdentity } from "./TesterIdentityProvider";
import { getStatusDisplay } from "./statusMap";
import { isAuthErrorCode } from "./authErrors";
import SessionExpiredPanel from "./SessionExpiredPanel";

const formatRelative = (timestamp) => {
  if (!timestamp?.toMillis) {
    return "";
  }
  const diffMs = Date.now() - timestamp.toMillis();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) {
    return "just now";
  }
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const isClosedBug = (bug) => bug.status === "closed";

const BugList = ({ selectedBugId, onSelect }) => {
  const { internalDb, tester } = useTesterIdentity();
  const [bugs, setBugs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [errorCode, setErrorCode] = useState(null);
  const [tab, setTab] = useState("open");
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    if (!internalDb || !tester?.id) {
      setBugs([]);
      setLoaded(false);
      setErrorCode(null);
      return undefined;
    }
    const bugsQuery = query(
      collection(internalDb, "tester-bugs"),
      where("testerId", "==", tester.id)
    );
    const unsubscribe = onSnapshot(
      bugsQuery,
      (snap) => {
        const docs = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
        const sorted = docs.sort(
          (a, b) =>
            (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)
        );
        setBugs(sorted);
        setErrorCode(null);
        setLoaded(true);
      },
      (err) => {
        console.warn("[tester-chat] bugs subscription error:", err.message);
        setBugs([]);
        setErrorCode(err.code || "unknown");
        setLoaded(true);
      }
    );
    return unsubscribe;
  }, [internalDb, tester?.id, retryToken]);

  const openBugs = useMemo(() => bugs.filter((bug) => !isClosedBug(bug)), [bugs]);
  const closedBugs = useMemo(() => bugs.filter(isClosedBug), [bugs]);
  const shown = tab === "open" ? openBugs : closedBugs;

  if (!loaded) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="textSecondary">
          Loading your bugs…
        </Typography>
      </Box>
    );
  }

  if (isAuthErrorCode(errorCode)) {
    return (
      <SessionExpiredPanel
        message="Your tester session expired. Sign in again to load your bugs."
        onReauthed={() => setRetryToken((prev) => prev + 1)}
      />
    );
  }

  if (errorCode) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="error" gutterBottom>
          Something went wrong loading your bugs.
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Refresh the page to retry.
        </Typography>
      </Box>
    );
  }

  if (bugs.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Welcome! When you find something that looks wrong, click "Report a
          bug" up top and describe what you saw.
        </Typography>
        <Typography variant="body2" color="textSecondary">
          I'll ask a few questions if I need more detail, dispatch a fix to
          test.aclymate.app, and ping you here once it's live so you can
          re-verify.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Tabs
        value={tab}
        onChange={(_, next) => setTab(next)}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: "divider", minHeight: 36 }}
      >
        <Tab
          value="open"
          label={`Open${openBugs.length ? ` (${openBugs.length})` : ""}`}
          sx={{ minHeight: 36, py: 0.5 }}
        />
        <Tab
          value="closed"
          label={`Closed${closedBugs.length ? ` (${closedBugs.length})` : ""}`}
          sx={{ minHeight: 36, py: 0.5 }}
        />
      </Tabs>
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {shown.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="textSecondary">
              {tab === "open"
                ? "No open bugs — nice. Report a new one with the button above when something looks wrong."
                : "No closed bugs yet."}
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {shown.map((bug) => {
              const { label, color } = getStatusDisplay(bug.status);
              return (
                <ListItemButton
                  key={bug.id}
                  selected={bug.id === selectedBugId}
                  onClick={() => onSelect(bug.id)}
                  divider
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" noWrap>
                        {bug.description || "(no description)"}
                      </Typography>
                    }
                    secondary={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 0.5
                        }}
                      >
                        <Chip label={label} size="small" color={color} />
                        <Typography variant="caption" color="textSecondary">
                          {formatRelative(bug.createdAt)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default BugList;
